import React from "react";
import { Modal, Button, Alert } from "@yoast/ui-library";
import PropTypes from "prop-types";
import { FormattedMessage, defineMessages, injectIntl } from "react-intl";
import * as styles from "./RevokeGrantModalStyles.module.scss";
import { getRedirectHostnames } from "./grantUtils";

const messages = defineMessages( {
	title: {
		id: "revokeGrant.title",
		defaultMessage: "Disconnect {appName}?",
	},
	description: {
		id: "revokeGrant.description",
		defaultMessage: "This will disconnect {appName} ({url}) from your MyYoast account. The application will no longer be able to access your data.",
	},
	descriptionNoUrl: {
		id: "revokeGrant.descriptionNoUrl",
		defaultMessage: "This will disconnect {appName} from your MyYoast account. The application will no longer be able to access your data.",
	},
	delayNote: {
		id: "revokeGrant.delayNote",
		defaultMessage: "It may take up to 15 minutes for all access to be fully revoked.",
	},
	reconnectNote: {
		id: "revokeGrant.reconnectNote",
		defaultMessage: "You can reconnect this application later by signing in again through the application.",
	},
	cancel: {
		id: "revokeGrant.cancel",
		defaultMessage: "Cancel",
	},
	confirm: {
		id: "revokeGrant.confirm",
		defaultMessage: "Disconnect",
	},
	ariaLabel: {
		id: "revokeGrant.ariaLabel",
		defaultMessage: "Disconnect application",
	},
} );

/**
 * Gets the display URL(s) from a grant.
 * If the clientUri hostname is yoast.com, shows redirect URI hostnames instead.
 * Otherwise shows the client URI hostname.
 *
 * @param {Object} grant The grant object.
 *
 * @returns {string|null} The URL(s) to display, or null if none available.
 */
function getDisplayUrl( grant ) {
	let clientHostname = null;
	if ( grant.clientUri ) {
		try {
			clientHostname = new URL( grant.clientUri ).hostname;
		} catch {
			// Invalid URL, fall through
		}
	}

	// If client is from yoast.com, show redirect URIs instead
	if ( clientHostname === "yoast.com" ) {
		const hostnames = getRedirectHostnames( grant.redirectUris );
		if ( hostnames.length > 0 ) {
			return hostnames.join( ", " );
		}
	}

	return clientHostname;
}

/**
 * Modal for confirming disconnection of an OAuth grant.
 *
 * Displays the application name and URL, with Cancel and Confirm actions.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {Object} props.grant The grant to disconnect.
 * @param {Function} props.onClose Callback when modal is closed.
 * @param {Function} props.onConfirm Callback when disconnect is confirmed.
 * @param {boolean} props.isRevoking Whether disconnection is in progress.
 * @param {string} props.error Error message if disconnection failed.
 * @param {Object} props.intl The react-intl object.
 *
 * @returns {JSX.Element|null} The modal component or null if no grant.
 */
function RevokeGrantModal( { isOpen, grant, onClose, onConfirm, isRevoking, error, intl } ) {
	if ( ! grant ) {
		return null;
	}

	const displayUrl = getDisplayUrl( grant );

	return (
		<Modal
			isOpen={ isOpen }
			onClose={ onClose }
			role="alertdialog"
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel className={ styles.panel }>
				<Modal.Title as="h2">
					<FormattedMessage { ...messages.title } values={ { appName: grant.clientName } } />
				</Modal.Title>

				<div className={ styles.body }>
					<p>
						{ displayUrl ? (
							<FormattedMessage
								{ ...messages.description }
								values={ { appName: grant.clientName, url: displayUrl } }
							/>
						) : (
							<FormattedMessage
								{ ...messages.descriptionNoUrl }
								values={ { appName: grant.clientName } }
							/>
						) }
					</p>

					<p className={ styles.note }>
						<FormattedMessage { ...messages.delayNote } />
					</p>

					<p className={ styles.note }>
						<FormattedMessage { ...messages.reconnectNote } />
					</p>

					{ error && (
						<Alert type="error" className={ styles.error }>
							{ error }
						</Alert>
					) }
				</div>

				<div className={ styles.actions }>
					<Button
						variant="secondary"
						onClick={ onClose }
						disabled={ isRevoking }
					>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						variant="error"
						onClick={ onConfirm }
						isLoading={ isRevoking }
						disabled={ isRevoking }
					>
						<FormattedMessage { ...messages.confirm } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

RevokeGrantModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	grant: PropTypes.shape( {
		grantId: PropTypes.string.isRequired,
		clientName: PropTypes.string.isRequired,
		clientUri: PropTypes.string,
		redirectUris: PropTypes.arrayOf( PropTypes.string ),
	} ),
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	isRevoking: PropTypes.bool.isRequired,
	error: PropTypes.string,
	intl: PropTypes.object.isRequired,
};

RevokeGrantModal.defaultProps = {
	grant: null,
	error: null,
};

export default injectIntl( RevokeGrantModal );
